import { Collapse, Dialog, DialogContent, IconButton, Skeleton, Table, TableBody, TableCell, TableContainer, TableFooter, TableHead, TablePagination, TableRow, Tooltip } from '@mui/material'
import React, { Fragment, useEffect, useState } from 'react'
import { useNotify, useTranslate } from 'react-admin'
import { ShowCard } from 'src/components/uiElements/cards/ShowCard'
import { numberFormatterWithDecimalAndThousand } from 'src/helper/numberFormatterWithDecimalAndThousand'
import { parseDateFormat } from 'src/helper/parseDateFormat'
import { itemSupplierServices } from 'src/services/purchases/item_supplier/ItemSupplier.service'
import { itemSupplierService } from "src/services/reservation/itemSupplier/itemSupplier.service"
import { StatusTableCell } from '../../helper/StatusTableCell'
import { TableDueDate } from '../payment/logic/ItemBalances'
import { Close, Download, Edit, InsertDriveFileOutlined, KeyboardArrowDown, KeyboardArrowUp, Visibility } from '@mui/icons-material'
import { getField } from '../payment/helper/getField'
import RefreshIcon from '@mui/icons-material/Refresh';
import { LoadingButton } from '@mui/lab'
import { EXPIRED, REJECTED, FAILED_INVOICED, CONFIRMED } from '../common/state'
import { HasPermission } from 'src/helper/HasPermission'
import PaymentsIcon from '@mui/icons-material/Payments';
import { ItemsToPayModal } from './logic/ItemsToPayModal'
import { GenerateJournalEntryModal } from './modals/GenerateJournalEntryModal'
import { DetailsJournalEntryModal } from './modals/DetailsJournalEntryModal'
import { InvalidateJournalEntryModal } from './modals/InvalidateJournalEntryModal'
import { EditItemSupplierModal } from './modals/EditItemSupplierModal'
import { ConfirmationDialog } from 'src/components/uiElements/styledComponents/ConfirmationDialog'

const paymentDetailsByType = {
  payment_cash: [
    'cash_name',
    'client_name',
    'client_document_type',
    'client_document_number',
  ],
  payment_bank: ['bank_name', 'coupon_number', 'date_time'],
  payment_online: [
    'vendor_code',
    'card_number',
    'quota',
    'bank_name',
    'expiry_date',
    'transaction',
    'agency_auth_code',
  ],
  payment_paypal: ['paypal_id', 'status_detail', 'payer.name.surname', 'date_time'],
  payment_braspag: [
    'card_number',
    'authorization_code',
    'installments',
    'card_holder',
    'card_brand_name',
    'customer_street',
    'customer_number',
    'customer_city',
    'customer_state',
    'customer_country',
    'customer_district',
    'customer_complement',
    'customer_zip_code',
  ],
  payment_pix: [
    'customer_document_type',
    'customer_document_value',
    'customer_name',
    'tx_id',
    'order_id',
    'date_time',
  ],
  payment_retention: [
    'type',
    'date_time',
    'description',
    'number',
  ],
}

const ItemBalanceRow = ({ item, i, refreshTable }) => {
    const translate = useTranslate()
    const [selectedItem, setSelectedItem] = useState()
    const [openDetailsJournalEntryModal, setOpenDetailsJournalEntryModal] = useState(false)
    const [openGenerateJournalEntryModal, setOpenGenerateJournalEntryModal] = useState(false)
    const [openInvalidateJournalEntryModal, setOpenInvalidateJournalEntryModal] = useState(false)
    const [openEditItemSupplierModal, setOpenEditItemSupplierModal] = useState(false)

    const journalEntries = item?.journal_entry ?? []
    const checkConfirmedJournalEntry = journalEntries?.some(elem => elem.status === CONFIRMED)

    useEffect(() => {
        if (
            !openDetailsJournalEntryModal &&
            !openGenerateJournalEntryModal &&
            !openInvalidateJournalEntryModal
        ) {
            setSelectedItem(null)
        }
    }, [openDetailsJournalEntryModal, openGenerateJournalEntryModal, openInvalidateJournalEntryModal])

    return (
        <Fragment key={i}>
            <TableRow>
                <TableCell>{item.detail?.name}</TableCell>
                <TableCell>{item.supplier_name}</TableCell>
                <TableCell>{item.code || item.detail?.code}</TableCell>
                <StatusTableCell data={item.status} align='center' style={{ width: 'fit-content', margin: 'auto' }} />
                <TableCell>{item.currency_relation?.short_name ?? ''}</TableCell>
                <TableCell align='right'>
                    {numberFormatterWithDecimalAndThousand(item.amount_total ?? 0)}
                </TableCell>
                <TableCell align='right'>
                    {numberFormatterWithDecimalAndThousand(item.amount_paid ?? 0)}
                </TableCell>
                <TableCell align='right'>
                    {numberFormatterWithDecimalAndThousand(item.amount_pending ?? 0)}
                </TableCell>
                <TableCell>{parseDateFormat(item.created_at)}</TableCell>
                <TableCell>
                    <TableDueDate
                        dueDate={item?.due_date}
                        amount_pending={item?.amount_pending}
                    />
                </TableCell>
                <TableCell>
                    <div style={{ display: 'flex', gap: '6px' }}>
                        <Tooltip placement='top' title={translate('ra.action.see_details')}>
                            <Visibility
                                color={journalEntries.length === 0 ? 'disabled' : 'primary'}
                                onClick={() => {
                                    if (journalEntries.length > 0) {
                                        setSelectedItem(item)
                                        setOpenDetailsJournalEntryModal(true)
                                    }
                                }}
                                style={{
                                    cursor: journalEntries.length === 0 ? 'default' : 'pointer',
                                    width: '20px'
                                }}
                            />
                        </Tooltip>
                        <HasPermission resource={'invoice'} permission='create'>
                            <Tooltip placement='top' title={translate('ra.action.generate_on_demand')}>
                                <InsertDriveFileOutlined
                                    color={checkConfirmedJournalEntry ? 'disabled' : 'primary'}
                                    onClick={() => {
                                        if (!checkConfirmedJournalEntry) {
                                            setSelectedItem(item)
                                            setOpenGenerateJournalEntryModal(true)
                                        }
                                    }}
                                    style={{
                                        cursor: checkConfirmedJournalEntry ? 'default' : 'pointer',
                                        width: '20px'
                                    }}
                                />
                            </Tooltip>
                        </HasPermission>
                        <HasPermission resource={'item-supplier'} permission='edit'>
                            <Tooltip placement='top' title={translate('ra.action.edit')}>
                                <Edit
                                    color='primary'
                                    onClick={() => setOpenEditItemSupplierModal(true)}
                                    style={{ cursor: 'pointer', width: '20px' }}
                                />
                            </Tooltip>
                        </HasPermission>
                        <HasPermission resource={'invoice'} permission='edit'>
                            <Tooltip placement='top' title={translate('ra.action.invalidate')}>
                                <Close
                                    color={checkConfirmedJournalEntry ? 'primary' : 'disabled'}
                                    onClick={() => {
                                        if (checkConfirmedJournalEntry) {
                                            setSelectedItem(item)
                                            setOpenInvalidateJournalEntryModal(true)
                                        }
                                    }}
                                    style={{
                                        cursor: checkConfirmedJournalEntry ? 'pointer' : 'default',
                                        width: '20px'
                                    }}
                                />
                            </Tooltip>
                        </HasPermission>
                    </div>
                </TableCell>
            </TableRow>

            <GenerateJournalEntryModal
                openModal={openGenerateJournalEntryModal}
                setOpenModal={setOpenGenerateJournalEntryModal}
                selectedItem={selectedItem}
                onComplete={() => refreshTable()}
            />

            <DetailsJournalEntryModal
                openModal={openDetailsJournalEntryModal}
                setOpenModal={setOpenDetailsJournalEntryModal}
                selectedItem={selectedItem}
            />

            <InvalidateJournalEntryModal
                openModal={openInvalidateJournalEntryModal}
                setOpenModal={setOpenInvalidateJournalEntryModal}
                selectedItem={selectedItem}
                onComplete={() => refreshTable()}
            />

            <EditItemSupplierModal
                item={item}
                open={openEditItemSupplierModal}
                setOpen={setOpenEditItemSupplierModal}
                onComplete={() => refreshTable()}
            />
        </Fragment>
    )
}

const ItemsBalancesTable = ({ reservation }) => {
    const translate = useTranslate()
    const [supplierItems, setSupplierItems] = useState([])
    const [totalRows, setTotalRows] = useState(0)
    const [supplierItemsLoading, setSupplierItemsLoading] = useState(false)
    const [paginationItemData, setPaginationItemData] = useState({ page: 0, rowsPerPage: 10, realPage:0 })

    const headers = [
        'name',
        'supplier',
        'code',
        'status',
        'currency',
        'amount',
        'amount_paid',
        'amount_pending',
        'created_at',
        'due_date',
        'actions'
    ]

    useEffect(()=>{
        if (reservation.items?.length > 0 && reservation.items[0].item_id) {
            getSupplierItems()
        }
    }, [paginationItemData, reservation])

    const getSupplierItems = async () =>{
        const items = reservation.items.map(item => item.item_id).join(',')
        setSupplierItemsLoading(true)
        let params = {
            limit:paginationItemData.rowsPerPage,
            skip:paginationItemData.realPage,
            item_id__in:items,
            journal_data:true
        }
        await itemSupplierService.getItemsSupplier(params).then(res => {
            setSupplierItems(res.data)
            setTotalRows(Number(res.total))
        }).finally(() => {
            setSupplierItemsLoading(false)
        })
    }

    const onPageItemChange = (number) => {
        const paginationItemDataAux = { ...paginationItemData }
        paginationItemDataAux.page = number
        paginationItemDataAux.realPage = number * paginationItemDataAux.rowsPerPage
        setPaginationItemData(paginationItemDataAux)
    }

    const onRowsPerPageItemChange = (e) => {
        const paginationItemDataAux = { ...paginationItemData }
        paginationItemDataAux.rowsPerPage = parseInt(e.target.value, 10)
        paginationItemDataAux.page = 0
        setPaginationItemData(paginationItemDataAux)
    }

    const getCurrencies = () => {
        const currencies = Array.from(
            new Set(
                supplierItems?.map(
                    (elem) => elem?.currency_relation?.short_name
                )
            )
        )
        return currencies
    }

    const getTotalCurrency = (field, currency) => {
        return supplierItems
            ?.filter((item) =>
                item?.status !== REJECTED
                && item?.status !== EXPIRED
                && item?.status !== FAILED_INVOICED
                )
            .filter(
                (elem) =>
                elem?.currency_relation?.short_name === currency
            )
            .reduce((acc, current) => {
                return acc + (current[field] || 0)
            }, 0)
    }

    return (
        <ShowCard
            title={translate('ra.title.item_balances')}
            preActionButton={
                <LoadingButton loading={supplierItemsLoading} onClick={getSupplierItems}>
                    <RefreshIcon />
                </LoadingButton>
            }
            preActionButtonJustify='flex-end'
        >
            <TableContainer>
                <Table>
                    <TableHead>
                        <TableRow>
                            {headers.map((header, i) => (
                                <TableCell key={i} align='center'>
                                    {translate(`ra.field.${header}`)}
                                </TableCell>
                            ))}
                        </TableRow>
                    </TableHead>

                    <TableBody>
                        {supplierItemsLoading ? (
                            <TableRow>
                                {Array.from({ length: 11 }).map((_,i)=>(
                                    <TableCell key={i}>
                                        <Skeleton variant='rectangular' height={40} width={'100%'}/>
                                    </TableCell>
                                ))}
                            </TableRow>
                        ) : (
                            supplierItems?.map((item, i) => (
                                <ItemBalanceRow
                                    key={i}
                                    i={i}
                                    item={item}
                                    refreshTable={() => getSupplierItems()}
                                />
                            ))

                        )}
                        {supplierItems?.length > 0 &&
                            getCurrencies().map((currency, i) => (
                                <TableRow key={i}>
                                    <TableCell colSpan={4} />
                                    <TableCell>
                                        <b>Total {currency}</b>
                                    </TableCell>
                                    <TableCell align='right'>
                                        {numberFormatterWithDecimalAndThousand(getTotalCurrency('amount_total', currency))}
                                    </TableCell>
                                    <TableCell align='right'>
                                        {numberFormatterWithDecimalAndThousand(getTotalCurrency('amount_paid', currency))}
                                    </TableCell>
                                    <TableCell align='right'>
                                        {numberFormatterWithDecimalAndThousand(getTotalCurrency('amount_pending', currency))}
                                    </TableCell>
                                </TableRow>
                            ))
                        }
                    </TableBody>

                    <TableFooter>
                        <TableRow>
                            <TablePagination
                                rowsPerPageOptions={[5, 10, 25, 50]}
                                rowsPerPage={paginationItemData.rowsPerPage}
                                page={paginationItemData.page}
                                count={totalRows}
                                onPageChange={(e, number) => onPageItemChange(number)}
                                onRowsPerPageChange={(e) => onRowsPerPageItemChange(e)}
                                labelRowsPerPage={translate('ra.navigation.page_rows_per_page')}
                                labelDisplayedRows={({ from, to, count }) =>
                                    `${from}-${to} ${translate('ra.navigation.of')} ${count}`
                                }
                            />
                        </TableRow>
                    </TableFooter>
                </Table>
            </TableContainer>
        </ShowCard>
    )
}

const InvoicesRow = ({ item, i, refreshTable }) => {
    const translate = useTranslate()
    const notify = useNotify()
    const [openConfirmationDialog, setOpenConfirmationDialog] = useState(false)
    const [confirmDeleteFile, setConfirmDeleteFile] = useState(false)
    const [loadingDeleteFile, setLoadingDeleteFile] = useState(false)

    useEffect(() => {
        if (confirmDeleteFile) {
            handleDeleteFile()
            setConfirmDeleteFile(false)
        }
    }, [confirmDeleteFile])

    const onGoToShow = () =>{
        window.open(`/#/supplier-billing/${item.supplier_billing_id}/show`, '_blank', 'noopener,noreferrer');
    }

    const handleDownloadFile = () => {
        const url = item.attachment_url
        const link = document.createElement('a')
        link.href = url
        link.target = '_blank'
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)
    }

    const handleDeleteFile = async () => {
        try {
            setLoadingDeleteFile(true)
            await itemSupplierServices.deleteAttatchmentInvoice(item.id)
            refreshTable?.()
            notify('ra.notification.delete_file_success', { type: 'success' })
        } catch (error) {
            notify('ra.notification.delete_file_error', { type: 'error' })
        } finally {
            setLoadingDeleteFile(false)
            setOpenConfirmationDialog(false)
        }
    }

    return (
        <>
            <TableRow key={i}>
                <TableCell>
                    {item.nro}
                </TableCell>
                <TableCell>
                    {item.type ? translate(`ra.field.${item.type.toLowerCase()}`) : ''}
                </TableCell>
                <TableCell>
                    {item.supplier_name}
                </TableCell>
                <TableCell>
                    {item.supplier_code}
                </TableCell>
                <TableCell>
                    {item.currency_relation?.short_name}
                </TableCell>
                <TableCell align='right'>
                    {numberFormatterWithDecimalAndThousand(item.amount)}
                </TableCell>
                <TableCell>
                    {parseDateFormat(item.invoice_date, null, false)}
                </TableCell>
                <TableCell>
                    <div style={{ display:'flex', gap: 10 }}>
                        <HasPermission resource={'supplier-billing'} permission='show'>
                            <Tooltip placement='top' title={translate('ra.action.show')}>
                                <Visibility color='primary' onClick={onGoToShow} style={{ cursor:'pointer', width:'20px' }}/>
                            </Tooltip>
                        </HasPermission>
                        {item?.attachment_url && (
                            <Tooltip placement='top' title={translate('ra.action.download_file')}>
                                <Download color='primary' onClick={handleDownloadFile} style={{ cursor:'pointer', width:'20px' }}/>
                            </Tooltip>
                        )}
                        {item?.attachment_url && (
                            <Tooltip placement='top' title={translate('ra.title.delete_file')}>
                                <Close color='error' onClick={() => setOpenConfirmationDialog(true)} style={{ cursor:'pointer', width:'20px' }}/>
                            </Tooltip>
                        )}
                    </div>
                </TableCell>
            </TableRow>

            <ConfirmationDialog
                title={translate('ra.title.delete_file')}
                contentText={translate('ra.message.delete_file_message')}
                buttonConfirmText={translate('ra.action.to_confirm')}
                buttonCancel={translate('ra.action.cancel')}
                setOpen={setOpenConfirmationDialog}
                loadingModalButton={loadingDeleteFile}
                setConfirm={setConfirmDeleteFile}
                open={openConfirmationDialog}
            />
        </>
    )
}

const InvoicesTable = ({ reservation }) => {
    const translate = useTranslate()
    const [invoices, setInvoices] = useState([])
    const [loadingInvoices, setLoadingInvoices] = useState(false)

    const itemsTitles = ['customer_document_value', 'document_type', 'supplier', 'provider_code', 'currency', 'amount', 'created_at', 'actions']

    useEffect(()=>{
        if (reservation.items.length > 0 && reservation.items[0].item_id) {
            getInvoices()
        }
    }, [reservation])

    const getInvoices = async () => {
        setLoadingInvoices(true)
        const itemsIds = reservation.items.map(item => item.item_id)
        await itemSupplierServices.getInvoicesByItem(itemsIds).then(res => {
            setInvoices(res.data)
        }).finally(() => {
            setLoadingInvoices(false)
        })
    }

    return (
        <ShowCard
            title={translate('ra.entity.supplier-billing')}
            preActionButton={
                <LoadingButton loading={loadingInvoices} onClick={getInvoices}>
                    <RefreshIcon />
                </LoadingButton>
            }
            preActionButtonJustify='flex-end'
        >
            <TableContainer>
                <Table>
                    <TableHead>
                        <TableRow>
                            {itemsTitles.map((title, i)=>(
                                <TableCell key={i}>
                                    {title && translate(`ra.field.${title}`)}
                                </TableCell>
                            ))}
                        </TableRow>
                    </TableHead>

                    <TableBody>
                        {loadingInvoices ? (
                            <TableRow>
                                {Array.from({ length: 8}).map((_,i)=>(
                                    <TableCell key={i}>
                                        <Skeleton variant='rectangular' height={40} width={'100%'}/>
                                    </TableCell>
                                ))}
                            </TableRow>
                        ) : (
                            React.Children.toArray(
                                invoices?.map((item, i) => (
                                    <InvoicesRow item={item} i={i} refreshTable={getInvoices} />
                                ))
                            )
                        )}
                    </TableBody>
                </Table>
            </TableContainer>
        </ShowCard>
    )
}

const TransactionRow = ({ item, i }) =>{
    const translate = useTranslate()
    const [openItemsModal, setOpenItemsModal] = useState(false)
    const [isExpanded, setIsExpanded] = useState(false)

    const transactionDetail = item?.supplier_payment_order_detail_relation

    const onExpandRow = () =>{
        setIsExpanded(!isExpanded)
    }

    const onGoToShow = () =>{
        window.open(`/#/supplier-payment-order/${item.id}/show`, '_blank', 'noopener,noreferrer');
    }

    return (
        <>
            <TableRow key={i}>
                <TableCell style={{ padding: '0px 16px', maxWidth: 50 }}>
                    {transactionDetail?.length > 0 ? (
                    <IconButton size='small' onClick={() => onExpandRow(item.id)}>
                        {isExpanded ? <KeyboardArrowUp /> : <KeyboardArrowDown />}
                    </IconButton>
                    ) : null}
                </TableCell>
                <TableCell>
                    {item.supplier_name}
                </TableCell>
                <TableCell>
                    {item.billing_code}
                </TableCell>
                <TableCell>
                    {item.currency_relation[0]?.short_name}
                </TableCell>
                <TableCell>
                    {item.amount}
                </TableCell>
                <StatusTableCell
                    data={item.status ? 'PAID' : 'PENDING'}
                    align='center'
                    style={{ width: 'fit-content', margin: 'auto' }}
                />
                <TableCell>
                    {parseDateFormat(item.created_at)}
                </TableCell>
                <TableCell >
                    <div style={{dispay:'flex', flexDirection:'row' ,justifyContent:'space-between', alignItems:'center', width:'100%'}}>
                        <HasPermission resource={'supplier-payment-order'} permission='show'>
                            <Tooltip placement='top' title={translate('ra.action.show')}>
                                <Visibility color='primary' onClick={onGoToShow} style={{cursor:'pointer', width:'20px', margin:'10px'}}/>
                            </Tooltip>
                        </HasPermission>
                        <Tooltip placement='top' title={translate('ra.title.list_item_to_pay')}>
                            <PaymentsIcon color='primary' onClick={()=>setOpenItemsModal(true)} style={{cursor:'pointer', width:'20px',margin:'10px'}}/>
                        </Tooltip>
                    </div>
                </TableCell>

            </TableRow>

            {isExpanded && (
                transactionDetail?.map((detail, j) => (
                    <TableRow key={j}>
                        <TableCell className='collapsible-row' colSpan={12}>
                            <Collapse in={isExpanded} unmountOnExit>
                                <div  style={{ display: 'flex', flexDirection: 'column' }} className='field'>
                                    <b>{translate('ra.field.id')}</b>
                                    <span>{detail?.payment_entity_relation?.id}</span>
                                </div>
                                <div style={{ display: 'flex', flexDirection: 'column' }} className='field'>
                                    <b>{translate('ra.field.date')}</b>
                                    <span>{parseDateFormat(detail?.payment_entity_relation?.payment_detail_relation?.date_time)}</span>
                                </div>
                                <div  style={{ display: 'flex', flexDirection: 'column' }} className='field'>
                                    <b>{translate('ra.field.payment_type')}</b>
                                    <span>{detail?.payment_entity_relation?.type}</span>
                                </div>
                                <div  style={{ display: 'flex', flexDirection: 'column' }} className='field'>
                                    <b>{translate('ra.field.currency')}</b>
                                    <span>{detail?.payment_entity_relation?.payment_detail_relation?.currency}</span>
                                </div>
                                <div style={{ display: 'flex', flexDirection: 'column' }} className='field'>
                                    <b>{translate('ra.field.amount')}</b>
                                    <span>{numberFormatterWithDecimalAndThousand(detail?.payment_entity_relation?.payment_detail_relation?.amount)}</span>
                                </div>
                                <div style={{ display: 'flex', flexDirection: 'column' }} className='field'>
                                    <b>{translate('ra.title.exchange_type')}</b>
                                    <span>{detail?.payment_entity_relation?.currencies_exchange?.[0]?.value}</span>
                                </div>
                                <div style={{ display: 'flex', flexDirection: 'column' }} className='field'>
                                    <b>{translate('ra.field.change_type_reversed')}</b>
                                    <span>{numberFormatterWithDecimalAndThousand(1/detail?.payment_entity_relation?.currencies_exchange?.[0]?.value)}</span>
                                </div>
                                {(paymentDetailsByType[detail?.payment_entity_relation?.ref_table_detail] || []).map((field, j) => {
                                    if (field === 'vendor_code') {
                                    return (
                                        <div key={j} style={{ display: 'flex', flexDirection: 'column' }} className='field'>
                                        <b>{detail?.payment_entity_relation?.type}</b>
                                        <span>{detail?.payment_entity_relation?.[field]}</span>
                                        </div>
                                    )
                                    }
                                    return getField(
                                        j,
                                        detail?.payment_entity_relation?.payment_detail_relation,
                                        field,
                                        translate
                                    )
                                })}

                            </Collapse>
                        </TableCell>
                    </TableRow>
                ))
            )}

            <Dialog open={openItemsModal} onClose={() => setOpenItemsModal(false)} maxWidth={'100%'}>
                <DialogContent>
                    <ItemsToPayModal id={item.id}/>
                </DialogContent>
            </Dialog>
            </>
    )
}

const TransactionTable = ({ reservation }) => {
    const translate = useTranslate()
    const [transactions, setTransactions] = useState([])
    const [loadingTransactions, setLoadingTransactions] = useState(false)
    const [total, setTotal] = useState(0)
    const [paginationItemData, setPaginationItemData] = useState({ page: 0, rowsPerPage: 10, realPage:0 })

    const itemsTitles = ['','supplier', 'code', 'currency', 'amount', 'status','created_at', 'actions']

    useEffect(()=>{
        if(reservation.items.length > 0){
            if(reservation.items[0].item_id)
                getTransactions()
        }
    },[paginationItemData,reservation])

    const getTransactions = async () =>{
        setLoadingTransactions(true)
        const items = reservation.items.map(item=>item.item_id).join(',')
        await itemSupplierServices.getTransactions({limit:paginationItemData.rowsPerPage, skip:paginationItemData.realPage, supplier_payment_order_item__item_id__in:items}).then(res=>{
            setTransactions(res.data)
            setTotal(Number(res.total))
        }).finally(()=>{
            setLoadingTransactions(false)
        })
    }

    const onPageItemChange = (number) => {
        const paginationItemDataAux = { ...paginationItemData }
        paginationItemDataAux.realPage = number * paginationItemDataAux.rowsPerPage
        paginationItemDataAux.page = number
        setPaginationItemData(paginationItemDataAux)
    }

    const onRowsPerPageItemChange = (e) => {
        const paginationItemDataAux = { ...paginationItemData }
        paginationItemDataAux.rowsPerPage = parseInt(e.target.value, 10)
        paginationItemDataAux.page = 0
        setPaginationItemData(paginationItemDataAux)
    }

    return (
        <ShowCard
            title={translate('ra.entity.supplier-payment-order')}
            preActionButton={
                <LoadingButton loading={loadingTransactions} onClick={getTransactions}>
                    <RefreshIcon />
                </LoadingButton>
            }
            preActionButtonJustify='flex-end'
        >
            <TableContainer>
                <Table>
                    <TableHead>
                        <TableRow>
                            {itemsTitles.map((title, i)=>(
                                <TableCell key={i}>
                                    {title && translate(`ra.field.${title}`)}
                                </TableCell>
                            ))}
                        </TableRow>
                    </TableHead>

                    <TableBody>
                        {loadingTransactions ? (
                            <TableRow>
                                {Array.from({ length: 8}).map((_,i)=>(
                                    <TableCell key={i}>
                                        <Skeleton variant='rectangular' height={40} width={'100%'}/>
                                    </TableCell>
                                ))}
                            </TableRow>
                        ) : (
                            React.Children.toArray(
                                transactions?.map((item, i) => (
                                    <TransactionRow item={item} i={i}/>
                                ))
                            )
                        )}
                    </TableBody>

                    <TableFooter>
                        <TableRow>
                            <TablePagination
                                rowsPerPageOptions={[5, 10, 25, 50]}
                                rowsPerPage={paginationItemData.rowsPerPage}
                                page={paginationItemData.page}
                                count={total}
                                onPageChange={(e, number) => onPageItemChange(number)}
                                onRowsPerPageChange={(e) => onRowsPerPageItemChange(e)}
                                labelRowsPerPage={translate('ra.navigation.page_rows_per_page')}
                                labelDisplayedRows={({ from, to, count }) =>
                                    `${from}–${to} ${translate('ra.navigation.of')} ${count}`
                                }
                            />
                        </TableRow>
                    </TableFooter>
                </Table>
            </TableContainer>
        </ShowCard>
    )
}

export const SuppliersTab = ({ reservation }) => {
    return (
        <div className='payment_reservation'>
            <ItemsBalancesTable reservation={reservation} />

            <InvoicesTable reservation={reservation} />

            <TransactionTable reservation={reservation} />
        </div>
    )
}
